import {
  ColumnData,
  SlidePanelRegistrar,
  useSlidePanel,
} from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { AssetCard } from '../../assets/components/assetCard';
import { AssetFilters } from '../../assets/components/assetFilters';
import { Issue } from '../../issues/components/issue';

const ACTIVITY_CONVERSATION_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/activityConversationPanel.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ISSUE_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/issues/components/issue.tsx';
const ASSET_CARD_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const ACTIVITY_CONVERSATION_ROUTE_PATTERN = '/activities/:issueId';
const ACTIVITY_ASSET_ROUTE_PATTERN = '/activities/:issueId/asset/:assetId';

export const ActivityConversationPanel = () => {
  const ctx = useOutletContext<SlidePanelRegistrar | null>();

  if (!ctx?.setSlidePanel) {
    return <ActivityConversationPanelLegacy />;
  }

  return <ActivityConversationPanelShell />;
};

const ActivityConversationPanelShell = () => {
  const { issueId, assetId } = useParams();
  const navigate = useNavigate();

  const rightColumn = useMemo<ColumnData | null>(() => {
    if (!issueId) return null;
    return {
      title: 'activities.detail.title',
      showHeader: false,
      disableScroll: true,
      // Wrap in AssetRouteProvider so Issue.onIssueDelete can navigate back
      // to /activities via listPath rather than relying on URL string checks.
      mainComponent: (
        <AssetRouteProvider value={{
          basePath: `/activities/${issueId}`,
          listPath: '/activities',
          isWorkspaceMode: false,
          issueId,
        }}>
          <Issue isComingFromActivities showAssetLink={!assetId} />
        </AssetRouteProvider>
      ),
      debug: {
        id: 'activity-conversation:right-issue',
        titleKey: 'activities.detail.title',
        component: 'Issue',
        componentSource: ISSUE_COMPONENT_SOURCE,
        columnSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        ownerComponent: 'ActivityConversationPanelShell',
        ownerSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        routePanelKey: 'activity-conversation',
        routePattern: assetId
          ? ACTIVITY_ASSET_ROUTE_PATTERN
          : ACTIVITY_CONVERSATION_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'ActivityConversationPanelShell',
          },
          column: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'rightColumn',
          },
          component: {
            file: ISSUE_COMPONENT_SOURCE,
            symbol: 'Issue',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
        entity: { type: 'activity', id: issueId },
      },
    };
  }, [issueId, assetId]);

  const thirdColumn = useMemo<ColumnData | null>(() => {
    if (!assetId) return null;
    return {
      title: 'assets.menu.titleSingular',
      showHeader: true,
      showBackButton: true,
      onBackButtonPress: () => navigate(`/activities/${issueId}`),
      headerComponent: <AssetFilters assetId={assetId} issueId={issueId} />,
      disableScroll: false,
      // Wrap in AssetRouteProvider so fold components inside AssetCard
      // can read issueId and basePath via useAssetRouting(). They can't
      // use useParams() here because AssetCard is rendered outside the
      // <Outlet> that provides the child route context.
      mainComponent: (
        <AssetRouteProvider value={{
          basePath: `/activities/${issueId}/asset/${assetId}`,
          listPath: '/activities',
          isWorkspaceMode: false,
          issueId,
        }}>
          <AssetCard assetId={assetId} />
        </AssetRouteProvider>
      ),
      debug: {
        id: 'activity-conversation:third-asset-card',
        titleKey: 'assets.menu.titleSingular',
        component: 'AssetCard',
        componentSource: ASSET_CARD_COMPONENT_SOURCE,
        columnSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        ownerComponent: 'ActivityConversationPanelShell',
        ownerSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        routePanelKey: 'activity-asset-card',
        routePattern: ACTIVITY_ASSET_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'ActivityConversationPanelShell',
          },
          column: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'thirdColumn',
          },
          component: {
            file: ASSET_CARD_COMPONENT_SOURCE,
            symbol: 'AssetCard',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
        entity: { type: 'asset', id: assetId },
      },
    };
  }, [assetId, issueId, navigate]);

  useSlidePanel('right', rightColumn, [rightColumn]);
  useSlidePanel('third', thirdColumn, [thirdColumn]);

  return null;
};

const ActivityConversationPanelLegacy = () => {
  const { issueId } = useParams();
  if (!issueId) return null;
  return (
    <AssetRouteProvider value={{
      basePath: `/activities/${issueId}`,
      listPath: '/activities',
      isWorkspaceMode: false,
      issueId,
    }}>
      <Issue isComingFromActivities />
    </AssetRouteProvider>
  );
};
